/**
 * Second-order springs for animation poses.
 *
 * Everything in the rig used to move with an exponential approach (`damp`),
 * which is stable and cheap and **cannot overshoot** — and overshoot, followed
 * by settling, is precisely what reads as weight. A figure that eases into
 * every pose looks like it is being interpolated, because it is.
 *
 * The integration is the closed-form solution of a damped harmonic oscillator,
 * not a stepped one. That matters here: an explicit Euler spring *gains* energy
 * and needs `dt < 2/ω`, which at 60 Hz caps the stiffness — and the game drops
 * frames exactly when the screen is busiest and the springs are stiffest. The
 * analytic form is stable at any timestep, including a 200 ms hitch.
 *
 * See docs/CHARACTERS.md §3.1.
 */

const LN2_4 = 4 * Math.LN2

/** A sprung scalar. `halflife` is how fast it chases; `zeta` how it arrives. */
export class Spring {
  value: number
  velocity = 0
  private goal: number

  /**
   * @param halflife seconds to halve the remaining distance — 0.05 is a snappy
   *   detail, 0.2 a heavy limb, 0.4 a sack.
   * @param zeta damping ratio: 1 arrives without overshoot, 0.7 springs without
   *   ringing, 2 is deliberately syrupy. The same three values Box2D ships.
   */
  constructor(
    initial: number,
    private halflife: number,
    private zeta = 1,
  ) {
    this.value = initial
    this.goal = initial
  }

  set(goal: number): void {
    this.goal = goal
  }

  /** Jumps to a value, keeping the velocity — used for impulses like a landing. */
  reset(value: number): void {
    this.value = value
    this.velocity = 0
  }

  step(dt: number): void {
    if (dt <= 0) return
    const damping = LN2_4 / this.halflife
    // Under-damped springs get their frequency from the damping ratio; the
    // critically damped case is the ζ = 1 limit of the same expression.
    const y = damping / (2 * this.zeta)
    const j0 = this.value - this.goal
    const j1 = this.velocity + j0 * y
    const e = Math.exp(-y * dt)
    this.value = e * (j0 + j1 * dt) + this.goal
    this.velocity = e * (this.velocity - j1 * y * dt)
  }
}

/** The same spring on an angle, chasing through the shortest arc. */
export class SpringAngle extends Spring {
  override set(goal: number): void {
    let delta = goal - this.value
    while (delta > Math.PI) delta -= Math.PI * 2
    while (delta < -Math.PI) delta += Math.PI * 2
    super.set(this.value + delta)
  }
}
